import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import theme from 'theme';
import { isMobileOrSmallScreen } from 'utils/deviceInfo';
import { Platform } from 'utils/platform';

import { DropDownItem, DropDownProps } from 'components/DropDown/interface';
import { Icons } from 'components/Icons';

let SelectDropdown: any = null;
if (Platform.OS !== 'web') {
  SelectDropdown = require('react-native-select-dropdown').default;
}

const WebDropDown: React.FC<DropDownProps> = (props) => {
  const { height, width } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DropDownItem | null>(null);
  const [buttonPosition, setButtonPosition] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    originalY: 0,
    buttonHeight: 0,
  });
  const buttonRef = useRef<TouchableOpacity>(null);
  const isMobile = isMobileOrSmallScreen(width);

  const safeItems = Array.isArray(props.items) ? props.items : [];

  useEffect(() => {
    if (props.defaultSelectedItem !== undefined && safeItems[props.defaultSelectedItem]) {
      setSelectedItem(safeItems[props.defaultSelectedItem]);
    }
  }, [props.defaultSelectedItem, safeItems]);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  const calculateDropdownPosition = useCallback(() => {
    return new Promise<void>((resolve) => {
      buttonRef.current?.measure((x, y, width, buttonHeight, pageX, pageY) => {
        const dropdownHeight = Math.min(safeItems.length * 44, 300);
        const spaceBelow = height - (pageY + buttonHeight);
        const spaceAbove = pageY;
        let finalY = pageY + buttonHeight + 2;
        let finalHeight = dropdownHeight;
        if (spaceBelow < dropdownHeight + (isMobile ? 70 : 0)) {
          finalY = pageY - dropdownHeight - 2;
          finalHeight = Math.min(dropdownHeight, spaceAbove - 10);
        } else {
          finalHeight = Math.min(dropdownHeight, spaceBelow - 10);
        }

        setButtonPosition({
          x: pageX,
          y: finalY,
          width: width,
          height: finalHeight,
          originalY: pageY,
          buttonHeight: buttonHeight,
        });
        resolve();
      });
    });
  }, [height, safeItems.length]);

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [height, width, isOpen, calculateDropdownPosition]);

  const handleSelect = (item: DropDownItem) => {
    setSelectedItem(item);
    setIsOpen(false);
    props.onChangeSelectedItem?.(item);
  };

  const handleToggle = async () => {
    if (safeItems.length > 0) {
      await calculateDropdownPosition();
      setIsOpen(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <View style={[styles.container, isMobile ? styles.containerMobile : styles.containerDesktop]}>
      <TouchableOpacity
        ref={buttonRef}
        onPress={handleToggle}
        disabled={!safeItems.length}
        style={[
          styles.dropdownButton,
          {
            borderColor: theme.colors.base.blue,
            backgroundColor: theme.colors.base.darkBlue,
          },
          !safeItems.length && styles.disabled,
        ]}>
        <Text style={styles.dropdownButtonText} numberOfLines={1}>
          {selectedItem?.label || props.emptyLabel || ''}
        </Text>
        <Icons.ChevronDown color={theme.colors.base.blue} />
      </TouchableOpacity>

      <Modal visible={isOpen} transparent={true} animationType="fade" onRequestClose={handleClose}>
        <Pressable style={styles.overlay} onPress={handleClose}>
          <View
            style={[
              styles.modalContainer,
              {
                position: 'absolute',
                left: buttonPosition.x,
                top: buttonPosition.y,
                width: buttonPosition.width,
              },
            ]}>
            <View
              style={[
                styles.dropdownMenu,
                {
                  height: buttonPosition.height,
                  backgroundColor: theme.colors.base.darkBlue,
                  borderColor: theme.colors.base.blue,
                  borderWidth: 1,
                },
              ]}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {safeItems.map((item, _index) => (
                  <TouchableOpacity
                    key={item.id}
                    onPress={() => handleSelect(item)}
                    style={[
                      styles.dropdownItem,
                      { paddingHorizontal: isMobile ? 60 : 80, paddingVertical: 0 },
                    ]}>
                    <Text style={styles.dropdownItemText}>{item.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const MobileDropDown: React.FC<DropDownProps> = (props) => {
  const { width } = useWindowDimensions();
  const dropdownRef = useRef<any>(null);
  const isMobile = isMobileOrSmallScreen(width);

  const safeItems = Array.isArray(props.items) ? props.items : [];

  useEffect(() => {
    if (props.defaultSelectedItem !== undefined && dropdownRef.current && safeItems.length > 0) {
      dropdownRef.current.selectIndex(props.defaultSelectedItem);
    }
  }, [props.defaultSelectedItem, safeItems.length]);

  const handleSelect = (selectedItem: any, _index: number) => {
    props.onChangeSelectedItem?.(selectedItem);
  };

  return (
    <View style={[styles.container, isMobile ? styles.containerMobile : styles.containerDesktop]}>
      <SelectDropdown
        ref={dropdownRef}
        data={safeItems}
        onSelect={handleSelect}
        searchPlaceHolder=""
        renderButton={(selectedItem: any, _isOpened: boolean) => {
          return (
            <View
              style={[
                styles.dropdownButton,
                {
                  borderColor: theme.colors.base.blue,
                  backgroundColor: theme.colors.base.darkBlue,
                },
              ]}>
              <Text style={styles.dropdownButtonText} numberOfLines={1}>
                {selectedItem?.label || props.emptyLabel || ''}
              </Text>
              <Icons.ChevronDown color={theme.colors.base.blue} />
            </View>
          );
        }}
        renderItem={(item: any, _index: number, _isSelected: boolean) => {
          return (
            <View
              style={{
                alignItems: 'center',
                borderColor: theme.colors.base.darkBlue,
                justifyContent: 'center',
                paddingVertical: 20,
              }}>
              <Text style={styles.dropdownItemText}>{item.label}</Text>
            </View>
          );
        }}
        showsVerticalScrollIndicator={false}
        dropdownStyle={{
          borderRadius: 10,
          backgroundColor: '#070D31',
          height: 300,
        }}
        dropdownOverlayColor="rgba(0, 0, 0, 0.3)"
        statusBarTranslucent={false}
        disableAutoScroll={true}
        disabled={!safeItems.length}
      />
    </View>
  );
};

const DropDown: React.FC<DropDownProps> = (props) => {
  if (Platform.OS === 'web') {
    return <WebDropDown {...props} />;
  }

  return <MobileDropDown {...props} />;
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
    maxWidth: 400,
    position: 'relative',
  },
  containerMobile: {
    marginHorizontal: 8,
    marginVertical: 16,
  },
  containerDesktop: {
    marginHorizontal: 8,
    marginVertical: 16,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 2,
    minHeight: 50,
  },
  dropdownButtonText: {
    color: 'white',
    fontSize: 16,
    flex: 1,
  },
  disabled: {
    opacity: 0.5,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContainer: {
    maxWidth: 400,
  },
  dropdownMenu: {
    borderRadius: 8,
    borderWidth: 0,
    maxHeight: 300,
  },
  dropdownItem: {
    paddingVertical: 12,
    justifyContent: 'center',
    backgroundColor: 'transparent',
    minHeight: 44,
  },
  dropdownItemText: {
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    fontFamily: 'system-ui',
    lineHeight: 22,
  },
});

export default DropDown;
